import { useEffect, useState } from 'react';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  FeatureSubscriptionPaymentCycle,
  FeatureSubscriptionProductCode,
  FeatureSubscriptionType,
  useFeatureSubscription_GetFeatureSubscriptionsByPortalIdLazyQuery,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
} from 'queries';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { NodeEnvironment } from 'utils/enum';
import {
  FEATURE_SUBSCRIPTION_ADD_ONS,
  getPlanOptionIndex,
  getProductCodeForProduction,
} from 'utils/feature-subscription';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import CartSummary from './components/cart-summary';
import CheckoutFormContainer from './components/checkout-form-container';
import { PayCycleSelection } from './components/pay-cycle-selection';
import { PlanOverview } from './components/plan-overview';
import { PlanSelection } from './components/plan-selection';

const useStyles = makeStyles(() => ({
  gridContainer: {
    marginLeft: '30px',
    marginBottom: '75px',
  },
  gridItem: {
    padding: '0px',
    margin: '10px',
  },
  listNumber: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ddd',
    borderRadius: '50%',
    width: '25px',
    height: '25px',
    marginRight: '15px',
  },
  orderedListContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: '10px',
  },
  pageContainer: {
    margin: '15px',
  },
  textFieldGridContainer: {
    marginLeft: '30px',
    marginBottom: '75px',
    alignItems: 'center',
  },
  textFieldGridItems: {
    '&&': {
      paddingTop: '0px',
    },
  },
  textField: {
    width: '50px',
    height: '50px',
    borderRadius: '15px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: '15px',
    },
    '& .MuiOutlinedInput-input': {
      textAlign: 'center',
    },
    '& input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
    },
    '& input::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
    },
  },
  textFieldLabel: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '10px',
  },
}));

const SubscriptionCheckoutPage = () => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();
  const location = useLocation();
  const queryString = new URLSearchParams(location.search);
  const isUpdating = queryString.get('isUpdating') ? true : false;
  const [currentPlan, setCurrentPlan] = useState<FeatureSubscriptionProductCode | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<FeatureSubscriptionProductCode>(
    (queryString.get('productCode') as FeatureSubscriptionProductCode) ||
      FeatureSubscriptionProductCode.SoloPlan,
  );
  const [additionalSeats, setAdditionalSeats] = useState<number>(0);
  const [selectedPayCycle, setSelectedPayCycle] = useState<FeatureSubscriptionPaymentCycle>(
    (queryString.get('payCycle') as FeatureSubscriptionPaymentCycle) ||
      FeatureSubscriptionPaymentCycle.Yearly,
  );
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [disablePayCycleSelection, setDisablePayCycleSelection] = useState(false);

  const { data: featureSubscriptionTypeData, loading: featureSubscriptionTypeLoading } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscription type: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  /**
   * Returns the 'next level' subscription level based on the current plan
   * @param currentPlan
   * @returns
   */
  // TODO need guidance on where the legacy plans should default for upgrading
  const determineSelectedPlanLevel = (currentPlan: FeatureSubscriptionProductCode) => {
    if (currentPlan === FeatureSubscriptionProductCode.TrialPlan) {
      return FeatureSubscriptionProductCode.SoloPlan;
    } else if (
      currentPlan === FeatureSubscriptionProductCode.LitePlan ||
      currentPlan === FeatureSubscriptionProductCode.SoloPlanLegacy ||
      currentPlan === FeatureSubscriptionProductCode.SoloPlan
    ) {
      return FeatureSubscriptionProductCode.SmallFirmPlan;
    } else if (
      currentPlan === FeatureSubscriptionProductCode.SmallFirmPlan ||
      currentPlan === FeatureSubscriptionProductCode.ProfessionalPlan ||
      currentPlan === FeatureSubscriptionProductCode.ProfessionalPlusPlan
    ) {
      return FeatureSubscriptionProductCode.MidLargeFirmPlan;
    } else {
      return FeatureSubscriptionProductCode.MidLargeFirmPlan;
    }
  };

  // Current subscription is loaded when updating subscription information
  const [loadSubscriptionByPortalId] =
    useFeatureSubscription_GetFeatureSubscriptionsByPortalIdLazyQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        if (!data.data || !data.data.length) {
          showAlert({
            message: 'Error getting feature subscriptions by portal ID.',
            severity: NotificationSeverity.ERROR,
          });
          return;
        }

        const subscription = data.data[0];
        const products = data.data[0].products;
        if (!products || !products.length) {
          showAlert({
            message: 'Error getting feature subscriptions by portal ID (no products).',
            severity: NotificationSeverity.ERROR,
          });
        }

        // check currently selected plan, exclude add ons
        const subscriptionPlan = products.find(
          (product) =>
            product.isActive && !FEATURE_SUBSCRIPTION_ADD_ONS.includes(product.stripeProductId!),
        );
        if (subscriptionPlan) {
          const currentPlan = getProductCodeForProduction(subscriptionPlan.stripeProductId!);
          if (currentPlan) {
            // Set the current plan and the default selected plan as 1 level higher than current
            setCurrentPlan(currentPlan);
            setSelectedPlan(determineSelectedPlanLevel(currentPlan));

            // lock updates to yearly or monthly
            // set pay cycle
            setSelectedPayCycle(
              subscription.isAnnualSubscription
                ? FeatureSubscriptionPaymentCycle.Yearly
                : FeatureSubscriptionPaymentCycle.Monthly,
            );
          }
        }

        // check for any additional seats and set the text field to the current number of seats for user adjustment
        const additionalSeatProduct = products.find(
          (product) =>
            product.productDescription ===
            (FeatureSubscriptionProductCode.AdditionalSeats ||
              (FeatureSubscriptionProductCode.AdditionalSeatsTest && product.isActive)),
        );
        setAdditionalSeats(additionalSeatProduct?.productQuantity || 0);
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscriptions by portal ID: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  useEffect(() => {
    if (isUpdating) {
      const loadFeatureSubscription = async () => {
        await loadSubscriptionByPortalId({ variables: { portalId: portalId } });
      };
      loadFeatureSubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!featureSubscriptionTypeData && featureSubscriptionTypeLoading) {
    return <Spinner />;
  }

  if (
    !isUpdating &&
    featureSubscriptionTypeData!.data !== FeatureSubscriptionType.None &&
    process.env.REACT_APP_NODE_ENV === NodeEnvironment.PROD
  ) {
    return (
      <Redirect
        to={{
          pathname: '/',
          state: { from: location.pathname },
        }}
      />
    );
  }

  const handlePlanOnClick = (productCode: FeatureSubscriptionProductCode) => {
    setDisablePayCycleSelection(false);
    setSelectedPlan(productCode);
    if (
      getProductCodeForProduction(productCode) ===
      getProductCodeForProduction(FeatureSubscriptionProductCode.TrialPlan)
    ) {
      setSelectedPayCycle(FeatureSubscriptionPaymentCycle.Monthly);
      setDisablePayCycleSelection(true);
    }
  };

  const handlePayCycleOnClick = (payCycle: FeatureSubscriptionPaymentCycle) => {
    setSelectedPayCycle(payCycle);
  };

  return (
    <HomeLayout>
      <div className={classes.pageContainer}>
        <Grid container spacing={2}>
          {!confirmingPayment && (
            <Grid item xs={12} sm={8} order={{ xs: 1, sm: 1 }}>
              <Grid container direction="column" spacing={2}>
                {isUpdating && (
                  <PlanOverview
                    currentPlan={currentPlan}
                    order={1}
                    portalId={portalId}
                    selectedPayCycle={selectedPayCycle}
                  />
                )}
                {getPlanOptionIndex(currentPlan) < 3 && (
                  <>
                    <PlanSelection
                      currentPlan={currentPlan}
                      isUpdating={isUpdating}
                      order={isUpdating ? 2 : 1}
                      handlePlanOnClick={handlePlanOnClick}
                      selectedPlan={selectedPlan}
                    />
                  </>
                )}
                {!disablePayCycleSelection && (
                  <PayCycleSelection
                    handlePayCycleOnClick={handlePayCycleOnClick}
                    isUpdating={isUpdating}
                    order={isUpdating ? 3 : 2}
                    selectedPlan={selectedPlan}
                    selectedPayCycle={selectedPayCycle}
                  />
                )}
              </Grid>
            </Grid>
          )}
          {confirmingPayment && (
            <Grid item xs={12} sm={8} order={{ xs: 1, sm: 1 }}>
              <Grid container direction="column" spacing={2}>
                {clientSecret ? (
                  <CheckoutFormContainer clientSecret={clientSecret!} />
                ) : (
                  <Spinner />
                )}
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} sm={4} order={{ xs: 3, sm: 3 }}>
            <CartSummary
              selectedPlan={selectedPlan}
              selectedPayCycle={selectedPayCycle}
              selectedAdditionalSeatQuantity={additionalSeats}
              confirmingPayment={confirmingPayment}
              setConfirmingPayment={setConfirmingPayment}
              setClientSecret={setClientSecret}
              isUpdating={isUpdating}
            />
          </Grid>
        </Grid>
      </div>
    </HomeLayout>
  );
};
export default SubscriptionCheckoutPage;
